<template>
  <div class="relative h-full">
    <div class="z-50 absolute w-full h-full bg-gray-200 bg-opacity-90 flex justify-center items-center" v-if="loadingMessage">
      <div class="text-xl font-bold">{{ loadingMessage }}</div>
    </div>
    <div class="py-4 select-none w-full">
      <div class="flex flex-col items-center justify-center min-h-screen">
        <template v-if="hidden">
          <h1 class="text-3xl font-bold text-info-600 mb-4">Tokenized Access</h1>
        </template>
        <template v-else-if="state === 'initial'">
          <h1 class="text-3xl font-bold text-blue-800 mb-4">Welcome to Newsdesk!</h1>
          <template v-if="hasUserData">
            <p class="text-lg text-center text-gray-700 mb-6">
              We detected that there are some session data stored on your device from your previous login session.<br />
              This means you might still have access to your previous session without needing to log in again.<br />
              To continue where you left off, please try logging in using the stored session data on your device.
            </p>
            <button
              class="bg-blue-800 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
              @click="redirectToDashboard"
            >
              Login with Stored Session
            </button>
            <p class="text-lg text-center text-gray-700 my-6">OR</p>
            <p class="text-lg text-center text-gray-700 mb-6">
              Login with tokenized access token. <br />
              This will allow us to verify your access and load the appropriate dashboard for you.
            </p>
            <button
              class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
              @click="checkTokenizedAccess"
            >
              Check Access Token
            </button>
          </template>
          <template v-else>
            <p class="text-lg text-center text-gray-700 mb-6">
              Please enter your tokenized access token or put it in URL to proceed.
              <br />
              This will allow us to verify your access and load the appropriate dashboard for you.
            </p>
            <div v-if="checkToken" class="flex flex-col">
              <input v-model="tokenFieldValue" class="border border-gray-400 p-2 text-gray-700 text-sm mb-4 rounded" />
              <button
                class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
                @click="checkTokenizedAccess"
              >
                Check Access Token
              </button>
            </div>
            <button
              v-else
              class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
              @click="toggleCheckToken"
            >
              Enter Access Token
            </button>
          </template>
        </template>
        <template v-else-if="state === 'check'">
          <h1 class="text-3xl font-bold text-blue-800 mb-4">Checking Access...</h1>
          <p class="text-lg text-center text-gray-700 mb-6">
            Please wait while we verify your tokenized access link.
            <br />
            This may take a few moments.
          </p>
        </template>
        <template v-else-if="state === 'success'">
          <h1 class="text-3xl font-bold text-green-600 mb-4">Access Granted</h1>
          <p class="text-lg text-center text-gray-700 mb-6">
            Welcome! Your tokenized access link has been successfully verified.
            <br />
            You now have access to the protected dashboard.
          </p>
          <router-link
            class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
            :to="{ name: '/' }"
            @click="redirectToDashboard"
            >Go to Homepage</router-link
          >
        </template>
        <template v-else-if="state === 'error'">
          <h1 class="text-3xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p class="text-lg text-center text-gray-700 mb-6">The token you have used is invalid or has expired.<br />Please contact your station manager.</p>
          <div v-if="checkToken" class="flex flex-col">
            <label for="token-field" class="text-gray-700">Enter access token:</label>
            <input id="token-field" v-model="tokenFieldValue" class="border border-gray-400 p-2 text-gray-700 text-sm mb-4 rounded" />
            <button
              class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
              @click="checkTokenizedAccess"
            >
              Check Access Token
            </button>
          </div>
          <button
            v-else
            class="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
            @click="toggleCheckToken"
          >
            Try Again
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import api from '@/config/api'
import localstore from 'store'

export default {
  components: {},
  data() {
    return {
      hidden: false,
      state: 'initial', // initial/check/success/error
      loadingMessage: false,
      tokenFieldValue: '',
      checkToken: false,
      hasUserData: false,
      redirectPath: ''
    }
  },
  computed: {
    token() {
      return this.$route.params.token || ''
    }
  },
  watch: {},
  async mounted() {
    const userData = localstore.get('ews_user', {})
    if (userData?.profile?.empid) {
      this.hasUserData = true
    } else if (this.token) {
      await this.checkTokenizedAccess()
    }
  },
  beforeUnmount() {},
  methods: {
    ...mapActions(['tokenizedAccessAuth']),
    redirectToDashboard() {
      if (this.redirectPath) {
        let redirectUrl = new URL(this.redirectPath)
        let newRoute = { path: redirectUrl.pathname, query: Object.fromEntries(redirectUrl.searchParams) }
        this.$router.push(newRoute).catch((err) => {})
        return
      }
      this.$router.push({ name: '/' }).catch(() => {})
      // this.$router.push({ path: '/' }).catch(() => {})
    },
    toggleCheckToken() {
      this.checkToken = !this.checkToken
    },
    async checkTokenizedAccess() {
      const tokenValue = this.tokenFieldValue || this.token || ''

      this.state = 'check'
      this.tokenFieldValue = ''
      // this.loadingMessage = 'Processing...'

      api
        .post(`/v2/auth/tokenized-access/generate-access/${tokenValue}`)
        .then((response) => {
          if (response.data.jwt) {
            const authData = {
              ews_auth_key: response.data.jwt || '',
              ews_auth_user: JSON.stringify({
                profile: response.data.profile || {},
                access: response.data.access || {}
              }),
              ews_auth_station: JSON.stringify(response.data.station || {}),
              ews_auth_container: JSON.stringify(response.data.container || {})
            }
            this.tokenizedAccessAuth(authData)
            this.redirectPath = response.data.path || ''

            const self = this
            setTimeout(function () {
              // wait for data stored
              self.state = 'success'
              setTimeout(function () {
                self.redirectToDashboard()
              }, 1000)
            }, 500)
          } else {
            this.state = 'error'
          }
        })
        .catch((error) => {
          this.state = 'error'
        })
    }
  }
}
</script>

<style lang="postcss" scoped></style>
